/* eslint-disable functional/no-throw-statements */
import { API_NAMES } from '../../api/api-names';
import {
  normalizeOriginRelativeBaseUrl,
  parseApiBaseUrls,
  parseKeycloakConfig,
  type StandaloneRuntimeConfig,
} from './config';

const DEFAULT_RUNTIME_CONFIG_URL = '/runtime-config.json';

/**
Loads and validates the standalone build's runtime configuration.

The federated build gets its config from Shell-supplied custom-element
attributes (see `remote-bootstrap.ts`'s `readRuntimeConfig`); the
standalone build has no Shell to supply them, so it instead fetches a
small JSON file served alongside the app itself. This keeps a single
standalone build artifact deployable to every environment — only the
served file differs, never a rebuild.

Reuses the same field-level validation (`normalizeOriginRelativeBaseUrl`/
`parseApiBaseUrls`) the Shell-attribute path already uses, adapted from
reading a DOM attribute string to reading a JSON field.
@param url - Location of the runtime configuration document.
@returns The validated standalone runtime configuration.
@throws {Error} If the document cannot be fetched, parsed, or fails validation.
*/
export async function loadStandaloneRuntimeConfig(
  url = DEFAULT_RUNTIME_CONFIG_URL,
): Promise<StandaloneRuntimeConfig> {
  const response = await fetchRuntimeConfig(url);

  if (!response.ok) {
    throw new Error(`Runtime configuration request to "${url}" failed: HTTP ${response.status}.`);
  }

  const raw: unknown = await parseJsonResponse(response, url);

  return parseStandaloneRuntimeConfig(raw, url);
}

/**
Fetches the runtime configuration document.
@param url - Location of the runtime configuration document.
@returns The raw HTTP response.
@throws {Error} If the request itself fails (network error, CORS, etc.).
*/
async function fetchRuntimeConfig(url: string): Promise<Response> {
  try {
    return await fetch(url, { headers: { Accept: 'application/json' } });
  } catch (error) {
    throw new Error(`Unable to load runtime configuration from "${url}".`, { cause: error });
  }
}

/**
Parses a runtime configuration response body as JSON.
@param response - The runtime configuration HTTP response.
@param url - Location of the runtime configuration document, used in the error message.
@returns The parsed JSON value.
@throws {Error} If the response body is not valid JSON.
*/
async function parseJsonResponse(response: Readonly<Response>, url: string): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new Error(`${url} does not contain valid JSON.`);
  }
}

/**
Validates and normalizes a parsed runtime configuration document.
@param value - The parsed JSON value to validate.
@param sourceUrl - Location of the runtime configuration document, used in error messages.
@returns The validated standalone runtime configuration.
@throws {TypeError} If the value is not a JSON object.
*/
function parseStandaloneRuntimeConfig(value: unknown, sourceUrl: string): StandaloneRuntimeConfig {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`${sourceUrl} must contain a JSON object.`);
  }

  const record = value as Record<string, unknown>;

  return Object.freeze({
    apiBaseUrl: normalizeOriginRelativeBaseUrl(
      readStringField(record['apiBaseUrl'], 'apiBaseUrl', sourceUrl),
      `${sourceUrl} apiBaseUrl`,
    ),
    i18nBaseUrl:
      record['i18nBaseUrl'] === undefined
        ? ''
        : normalizeOriginRelativeBaseUrl(
            readStringField(record['i18nBaseUrl'], 'i18nBaseUrl', sourceUrl),
            `${sourceUrl} i18nBaseUrl`,
          ),
    apiBaseUrls: parseApiBaseUrls(
      'apiBaseUrls' in record ? JSON.stringify(record['apiBaseUrls']) : null,
      `${sourceUrl} apiBaseUrls`,
      API_NAMES,
    ),
    catalogueRevision: readCatalogueRevision(record),
    keycloak: parseKeycloakConfig(record['keycloak'], `${sourceUrl} keycloak`),
  });
}

/**
Reads the optional catalogue revision field.
@param record - The parsed runtime configuration object.
@returns The trimmed revision, or `development` when absent/blank.
*/
function readCatalogueRevision(record: Readonly<Record<string, unknown>>): string {
  const value = record['catalogueRevision'];

  return typeof value === 'string' && value.trim() ? value.trim() : 'development';
}

/**
Validates a required string field from a parsed runtime configuration object.
@param value - The field value to validate.
@param field - Name of the field to read.
@param sourceUrl - Location of the runtime configuration document, used in the error message.
@returns The field's string value.
@throws {TypeError} If the field is absent or not a string.
*/
function readStringField(value: unknown, field: string, sourceUrl: string): string {
  if (typeof value !== 'string') {
    throw new TypeError(`${sourceUrl} must contain a string "${field}" field.`);
  }

  return value;
}
